import React from "react";
import { HashLink } from "./HashLink";
import { useLang } from "./LangContext";
import { useTenant } from "./TenantContext";
import { NAV_LINKS } from "./data";
import { Mail, MapPin, Instagram, Facebook } from "lucide-react";

export default function Footer() {
    const { lang, t } = useLang();
    const { tenant } = useTenant();
    const contact = tenant?.contact || {};
    const social = tenant?.social || {};
    const name = tenant?.name || "VisitTadasuni";
    return (
        <footer className="bg-stone-900 text-stone-200 mt-16" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-4 gap-10">
                <div className="md:col-span-2">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-emerald-500/40">
                            <img
                                src={tenant?.logo_url || "https://www.visittadasuni.it/wp-content/uploads/2025/11/pietra-logo-tadasuni.jpg"}
                                alt={`${name} logo`}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="font-serif text-3xl font-semibold text-white">{name}</div>
                    </div>
                    <p className="text-stone-400 max-w-md leading-relaxed">{tenant?.borgo?.region || t.footer_tag}</p>
                    {contact.address && (
                        <div className="flex items-center gap-3 mt-6 text-stone-300">
                            <MapPin size={18} className="text-emerald-400" />
                            <span className="text-sm">{contact.address}</span>
                        </div>
                    )}
                    {contact.email && (
                        <div className="flex items-center gap-3 mt-2 text-stone-300">
                            <Mail size={18} className="text-emerald-400" />
                            <a href={`mailto:${contact.email}`} className="text-sm hover:text-emerald-300 transition-colors">
                                {contact.email}
                            </a>
                        </div>
                    )}
                </div>

                <div>
                    <div className="text-xs font-semibold tracking-[0.2em] uppercase text-emerald-400 mb-4">
                        {lang === "it" ? "Esplora" : "Explore"}
                    </div>
                    <ul className="space-y-3">
                        {NAV_LINKS.map((l) => (
                            <li key={l.key}>
                                <HashLink
                                    to={l.route}
                                    hash={l.href}
                                    className="text-stone-300 hover:text-emerald-300 transition-colors text-sm"
                                    data-testid={`footer-${l.testid}`}
                                >
                                    {l.label[lang]}
                                </HashLink>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <div className="text-xs font-semibold tracking-[0.2em] uppercase text-emerald-400 mb-4">
                        {lang === "it" ? "Seguici" : "Follow us"}
                    </div>
                    <div className="flex items-center gap-3">
                        {social.instagram && (
                            <a href={social.instagram} target="_blank" rel="noreferrer"
                                className="w-10 h-10 rounded-full border border-stone-700 hover:border-emerald-500 hover:text-emerald-400 flex items-center justify-center transition-colors"
                                aria-label="Instagram" data-testid="footer-instagram">
                                <Instagram size={18} />
                            </a>
                        )}
                        {social.facebook && (
                            <a href={social.facebook} target="_blank" rel="noreferrer"
                                className="w-10 h-10 rounded-full border border-stone-700 hover:border-emerald-500 hover:text-emerald-400 flex items-center justify-center transition-colors"
                                aria-label="Facebook" data-testid="footer-facebook">
                                <Facebook size={18} />
                            </a>
                        )}
                    </div>
                    <p className="text-xs text-stone-500 mt-6">
                        © {new Date().getFullYear()} {name}. {lang === "it" ? "Tutti i diritti riservati." : "All rights reserved."}
                    </p>
                </div>
            </div>
        </footer>
    );
}
